import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { OverlayPanelModule } from 'primeng/overlaypanel';

export interface RestaurantCategory {
  cid: number | string;
  name: string;
  icon?: string;      // PrimeIcons class
  emoji?: string;     // fallback emoji (ignored per design requirement)
  color?: string;     // optional theme color for icon
  count?: number;
}

@Component({
  selector: 'app-category-tabs',
  standalone: true,
  imports: [CommonModule, OverlayPanelModule],
  templateUrl: './category-tabs.component.html',
  styleUrls: ['./category-tabs.component.scss'],
})
export class CategoryTabsComponent implements OnChanges {
  @ViewChild('tabsWrapper') tabsWrapper?: ElementRef<HTMLDivElement>;

  /** List of categories to display — driven by parent */
  @Input() categories: RestaurantCategory[] = [];

  /** Currently selected category id — parent controls selection */
  @Input() selectedCategoryId: number | string | null = null;

  /** How many categories to show before collapsing into "More" */
  @Input() visibleCount = 8;

  /** Emits the selected category id (null = "All") */
  @Output() categorySelected = new EventEmitter<number | string | null>();

  visibleCategories: RestaurantCategory[] = [];
  overflowCategories: RestaurantCategory[] = [];

  /** Synthetic "All" entry always shown first */
   ALL_CATEGORY: RestaurantCategory = { cid: '__all__', name: 'All', icon: 'pi pi-th-large' };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['categories'] || changes['visibleCount']) {
      const all = this.categories ?? [];
      this.visibleCategories = all.slice(0, this.visibleCount);
      this.overflowCategories = all.slice(this.visibleCount);
    }
  }

  isMouseDown = false;
  startX = 0;
  scrollLeftPos = 0;

  onWheel(event: WheelEvent): void {
    if (this.tabsWrapper?.nativeElement) {
      if (event.deltaY !== 0) {
        event.preventDefault();
        this.tabsWrapper.nativeElement.scrollLeft += event.deltaY;
      }
    }
  }

  onMouseDown(event: MouseEvent): void {
    if (!this.tabsWrapper?.nativeElement) return;
    this.isMouseDown = true;
    this.startX = event.pageX - this.tabsWrapper.nativeElement.offsetLeft;
    this.scrollLeftPos = this.tabsWrapper.nativeElement.scrollLeft;
  }

  onMouseLeave(): void {
    this.isMouseDown = false;
  }

  onMouseUp(): void {
    this.isMouseDown = false;
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isMouseDown || !this.tabsWrapper?.nativeElement) return;
    event.preventDefault();
    const x = event.pageX - this.tabsWrapper.nativeElement.offsetLeft;
    const walk = (x - this.startX) * 1.5;
    this.tabsWrapper.nativeElement.scrollLeft = this.scrollLeftPos - walk;
  }

  selectCategory(id: number | string | null): void {
    this.categorySelected.emit(id === '__all__' ? null : id);
  }

  isSelected(id: number | string | null): boolean {
    if (id === '__all__') return this.selectedCategoryId === null;
    return this.selectedCategoryId === id;
  }

  /**
   * Resolves a clean PrimeIcon class for any category, eliminating emojis entirely.
   */
  getCategoryIcon(cat: RestaurantCategory): string {
    if (cat.icon) return cat.icon;
    const name = (cat.name || '').toLowerCase();
    if (name.includes('all')) return 'pi pi-th-large';
    if (name.includes('starter') || name.includes('appetizer')) return 'pi pi-sparkles';
    if (name.includes('main')) return 'pi pi-box';
    if (name.includes('bread')) return 'pi pi-slack';
    if (name.includes('rice') || name.includes('biryani')) return 'pi pi-circle-fill';
    if (name.includes('beverage') || name.includes('drink') || name.includes('juice')) return 'pi pi-shopping-bag';
    if (name.includes('dessert') || name.includes('sweet') || name.includes('ice')) return 'pi pi-heart-fill';
    if (name.includes('soup')) return 'pi pi-sun';
    if (name.includes('salad') || name.includes('veg')) return 'pi pi-filter';
    if (name.includes('seafood') || name.includes('fish')) return 'pi pi-compass';
    if (name.includes('combo')) return 'pi pi-th-large';
    if (name.includes('fruit')) return 'pi pi-circle';
    if (name.includes('chicken') || name.includes('meat')) return 'pi pi-bolt';
    if (name.includes('mushroom')) return 'pi pi-shield';
    if (name.includes('dairy') || name.includes('milk')) return 'pi pi-box';
    if (name.includes('cookie') || name.includes('bakery')) return 'pi pi-star-fill';
    return 'pi pi-tag';
  }
}


